import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
// UUID validation
const uuidSchema = z.string().uuid('Invalid UUID format');
// ─── Analyze Request ───────────────────────────────────────────────────────
export const AnalyzeContentSchema = z
    .object({
    prompt: z
        .string()
        .min(3, 'Prompt must be at least 3 characters')
        .max(1000, 'Prompt must be less than 1000 characters')
        .trim()
        .optional()
        .openapi({
        description: 'User prompt (idea flow). Omit for file-only analysis.',
        example: 'Teach me about neural networks',
    }),
    context: z
        .string()
        .max(500, 'Context must be less than 500 characters')
        .trim()
        .optional()
        .openapi({
        description: 'Optional focus context for file flow (e.g. "focus on chapter 3")',
        example: 'Focus on the nervous system section',
    }),
    errorCorrection: z.coerce.boolean().optional().default(false).openapi({
        description: 'Allow AI to correct factual errors in uploaded content (file flow only)',
    }),
})
    // NOTE: Cannot validate prompt-or-file here because file is sent via multipart,
    // not in the JSON body. The controller validates at least one is provided.
    .openapi('AnalyzeContentRequest');
// ─── Refine Request ────────────────────────────────────────────────────────
export const AnalysisIdParamSchema = z.object({
    analysisId: uuidSchema.openapi({ description: 'Analysis record ID' }),
});
export const RefineAnalysisSchema = z
    .object({
    feedback: z
        .string()
        .min(3, 'Feedback must be at least 3 characters')
        .max(500, 'Feedback must be less than 500 characters')
        .trim()
        .openapi({
        description: 'User feedback to refine suggestions',
        example: 'I was looking for something about the nervous system specifically',
    }),
})
    .openapi('RefineAnalysisRequest');
// ─── Generate From Analysis Request ────────────────────────────────────────
export const GenerateFromAnalysisSchema = z
    .object({
    suggestionIndex: z
        .number()
        .int()
        .min(0, 'Suggestion index must be >= 0')
        .openapi({ description: 'Index of the selected suggestion', example: 0 }),
    imagesEnabled: z
        .boolean()
        .optional()
        .openapi({
        description: 'Learner toggle for including images in the lesson. Server clamps this against tier capability — Plus users always generate without images regardless of this flag.',
        example: true,
    }),
})
    .openapi('GenerateFromAnalysisRequest');
// ─── Suggestion Concept (atomic concept with description) ─────────────────
export const SuggestionConceptSchema = z
    .object({
    label: z.string().openapi({
        description: 'Short concept label',
        example: 'Plaque psoriasis',
    }),
    description: z.string().openapi({
        description: 'Brief description of what this concept covers',
        example: 'Appearance, common sites, distinguishing features',
    }),
})
    .openapi('SuggestionConcept');
// ─── Suggestion Shape (response) ───────────────────────────────────────────
export const LessonSuggestionSchema = z
    .object({
    index: z.number().int().openapi({ description: 'Position in the suggestions array' }),
    title: z.string().openapi({
        description: 'Descriptive lesson title',
        example: 'How the Heart Pumps Blood',
    }),
    shortDescription: z.string().openapi({
        description: 'One-line summary (max ~100 chars)',
        example: 'Covers cardiac cycle, valves, and blood flow',
    }),
    description: z.string().openapi({
        description: '1-2 sentence explanation of lesson scope and learning outcomes',
    }),
    concepts: z.array(SuggestionConceptSchema).openapi({
        description: 'Atomic concepts that will be taught and tested',
    }),
    estimatedMinutes: z.number().int().openapi({
        description: 'Estimated lesson duration in minutes',
        example: 18,
    }),
    sourcePages: z.string().optional().openapi({
        description: 'Page range from uploaded document (file flow only)',
        example: 'pages 10-18',
    }),
    availableFigures: z.number().int().optional().openapi({
        description: 'Number of figures available in the source section (file flow only)',
        example: 3,
    }),
    relevantExcerpt: z.string().optional().openapi({
        description: 'Key passages from the uploaded document relevant to this lesson (file flow only)',
    }),
    contentType: z.enum(['direct_teaching', 'curriculum_guide']).optional().openapi({
        description: 'Whether the source material is content to teach from or a curriculum to use as a guide',
    }),
})
    .openapi('LessonSuggestion');
// ─── Analysis Response ─────────────────────────────────────────────────────
export const AnalysisResultSchema = z
    .object({
    analysisId: uuidSchema.openapi({ description: 'Persisted analysis record ID' }),
    sourceType: z.enum(['prompt', 'file']).openapi({
        description: 'Whether analysis was from a prompt or file upload',
    }),
    suggestions: z.array(LessonSuggestionSchema).openapi({
        description: 'AI-suggested lessons (1 for focused topics, up to 3 for broad topics)',
    }),
})
    .openapi('AnalysisResult');
//# sourceMappingURL=personal-lesson-analysis.schema.js.map